/**
 * Validation and formatting utilities for IPI and ISNI identifiers.
 *
 * IPI (Interested Party Information) - specifically IPI Name Numbers:
 * - 11 digit identifier for rights holders (or 9 digits zero-padded to 11)
 * - Displayed as plain digits without separators
 * - Validated using weighted checksum mod 101 algorithm
 *
 * ISNI (International Standard Name Identifier):
 * - 16 character identifier for public identities
 * - Format: XXXX XXXX XXXX XXXX (groups of 4)
 * - Check character can be 0-9 or X
 * - Validated using ISO/IEC 7064 Mod 11-2 algorithm
 */

import type { ExternalIdentifier } from 'types/writer'

/**
 * IPI Field Constants
 */
export const IPI_LENGTH = 11
export const IPI_DISPLAY_LENGTH = 11

/**
 * ISNI Field Constants
 */
export const ISNI_LENGTH = 16
export const ISNI_DISPLAY_LENGTH = 19 // includes 3 spaces

/**
 * Strips all non-digit characters from a string.
 * Used for normalizing user input.
 */
export function stripNonDigits(value: string): string {
  return value.replace(/\D/g, '')
}

/**
 * Strips all non-alphanumeric characters from a string.
 * Used for normalizing ISNI input (which can end with X).
 */
export function stripNonAlphanumeric(value: string): string {
  return value.replace(/[^0-9Xx]/g, '').toUpperCase()
}

/**
 * Validates an IPI Name Number using the weighted checksum mod 101 algorithm.
 *
 * IPI Name Numbers are 11 digits (or 9 digits which get zero-padded to 11).
 * The algorithm:
 * 1. Take the first 9 digits
 * 2. Multiply each digit by weights (10, 9, 8, 7, 6, 5, 4, 3, 2)
 * 3. Sum the weighted values and take mod 101
 * 4. Calculate expected check: if sum is 0, check is 00; otherwise (101 - sum) % 100
 * 5. Last 2 digits should equal the calculated check value
 *
 * @param ipi - The IPI string (can include formatting characters)
 * @returns true if valid, false otherwise
 */
export function validateIPI(ipi: string): boolean {
  let digits = stripNonDigits(ipi)

  // IPI can be 9 or 11 digits; 9-digit IPIs are zero-padded to 11
  if (digits.length === 9) {
    digits = '00' + digits
  }

  // IPI must be exactly 11 digits after padding
  if (digits.length !== IPI_LENGTH) {
    return false
  }

  // Weighted checksum mod 101 algorithm
  const body = digits.slice(0, 9) // First 9 digits
  let total = 0

  for (let i = 0; i < 9; i++) {
    total += parseInt(body[i], 10) * (10 - i) // Weights: 10, 9, 8, 7, 6, 5, 4, 3, 2
  }

  total = total % 101
  if (total !== 0) {
    total = (101 - total) % 100
  }

  // Last 2 digits should equal the calculated total (zero-padded)
  const expectedCheck = total.toString().padStart(2, '0')
  const actualCheck = digits.slice(9, 11)

  return expectedCheck === actualCheck
}

/**
 * Validates an ISNI using ISO/IEC 7064 Mod 11-2 algorithm.
 *
 * Algorithm:
 * 1. Process digits left to right
 * 2. For each position: (previous_result + digit) * 2 mod 11
 * 3. Final check: (result + check_character) mod 11 should equal 1
 * 4. Check character is X if remainder is 10, otherwise the digit
 *
 * @param isni - The ISNI string (can include formatting characters)
 * @returns true if valid, false otherwise
 */
export function validateISNI(isni: string): boolean {
  const normalized = stripNonAlphanumeric(isni)

  // ISNI must be exactly 16 characters
  if (normalized.length !== ISNI_LENGTH) {
    return false
  }

  // First 15 characters must be digits
  const body = normalized.slice(0, 15)
  if (!/^\d{15}$/.test(body)) {
    return false
  }

  // Last character can be digit or X
  const checkChar = normalized[15]
  if (!/^[0-9X]$/.test(checkChar)) {
    return false
  }

  // ISO 7064 Mod 11-2 algorithm
  let result = 0
  for (let i = 0; i < 15; i++) {
    const digit = parseInt(normalized[i], 10)
    result = ((result + digit) * 2) % 11
  }

  // Calculate expected check digit
  const checkValue = (12 - result) % 11
  const expectedCheckChar = checkValue === 10 ? 'X' : checkValue.toString()

  return checkChar === expectedCheckChar
}

/**
 * Formats an IPI number for display as plain digits: XXXXXXXXXXX
 * IPI numbers are rendered without separators.
 *
 * @param ipi - Raw IPI digits
 * @returns Formatted IPI string (digits only)
 */
export function formatIPIDisplay(ipi: string): string {
  const digits = stripNonDigits(ipi)

  if (digits.length === 0) {
    return ''
  }

  return digits.slice(0, IPI_LENGTH)
}

/**
 * Formats an ISNI with display mask: XXXX XXXX XXXX XXXX
 * Groups into 4 character blocks separated by spaces.
 *
 * @param isni - Raw ISNI characters
 * @returns Formatted ISNI string
 */
export function formatISNIDisplay(isni: string): string {
  const normalized = stripNonAlphanumeric(isni)

  if (normalized.length === 0) {
    return ''
  }

  // Split into groups of 4 and join with spaces
  const groups: string[] = []
  for (let i = 0; i < normalized.length && i < ISNI_LENGTH; i += 4) {
    groups.push(normalized.slice(i, i + 4))
  }

  return groups.join(' ')
}

/**
 * Parses an IPI input back to raw digits.
 * Strips any non-digit characters.
 *
 * @param formattedIPI - IPI with display formatting
 * @returns Raw IPI digits
 */
export function parseIPIInput(formattedIPI: string): string {
  return stripNonDigits(formattedIPI).slice(0, IPI_LENGTH)
}

/**
 * Parses a formatted ISNI back to raw characters.
 * Removes display mask spaces.
 *
 * @param formattedISNI - ISNI with display formatting
 * @returns Raw ISNI characters (digits + optional X)
 */
export function parseISNIInput(formattedISNI: string): string {
  return stripNonAlphanumeric(formattedISNI).slice(0, ISNI_LENGTH)
}

/**
 * Gets validation error message for IPI.
 *
 * @param ipi - The IPI value to validate
 * @returns Error message if invalid, null if valid or empty
 */
export function getIPIValidationError(ipi: string): string | null {
  const digits = stripNonDigits(ipi)

  // Empty is valid (field is optional)
  if (digits.length === 0) {
    return null
  }

  // Check length - IPI can be 9 or 11 digits
  if (digits.length !== IPI_LENGTH && digits.length !== 9) {
    return 'IPI must be 9 or 11 digits'
  }

  // Check weighted checksum
  if (!validateIPI(digits)) {
    return 'Invalid IPI'
  }

  return null
}

/**
 * Gets validation error message for ISNI.
 *
 * @param isni - The ISNI value to validate
 * @returns Error message if invalid, null if valid or empty
 */
export function getISNIValidationError(isni: string): string | null {
  const normalized = stripNonAlphanumeric(isni)

  // Empty is valid (field is optional)
  if (normalized.length === 0) {
    return null
  }

  // Check length
  if (normalized.length !== ISNI_LENGTH) {
    return `ISNI must be ${ISNI_LENGTH} characters`
  }

  // Check format (15 digits + check character) & Check algorithm
  const body = normalized.slice(0, 15)
  if (!/^\d{15}$/.test(body) || !validateISNI(normalized)) {
    return 'Invalid ISNI'
  }

  return null
}

/**
 * Gets an identifier value from external_identifiers array by type.
 *
 * @param identifiers - Array of external identifiers
 * @param type - The identifier type to find ('ipi' or 'isni')
 * @returns The identifier value if found, undefined otherwise
 */
export function getIdentifierValue(
  identifiers: ExternalIdentifier[] | undefined,
  type: 'ipi' | 'isni',
): string | undefined {
  if (!identifiers || identifiers.length === 0) {
    return undefined
  }
  const identifier = identifiers.find(id => id.identifier_type === type)
  return identifier?.identifier_value
}

/**
 * Formats external identifiers for dropdown display.
 * Shows IPI and/or ISNI on separate lines with labels.
 *
 * Format:
 * - "IPI: XXXXXXXXXXX" (if only IPI)
 * - "ISNI: XXXX XXXX XXXX XXXX" (if only ISNI)
 * - "IPI: XXXXXXXXXXX\nISNI: XXXX XXXX XXXX XXXX" (if both)
 *
 * @param identifiers - Array of external identifiers from API
 * @returns Formatted string for display, or undefined if no identifiers
 */
export function formatIdentifiersForDisplay(
  identifiers: ExternalIdentifier[] | undefined,
): string | undefined {
  if (!identifiers || identifiers.length === 0) {
    return undefined
  }

  const lines: string[] = []

  const ipi = getIdentifierValue(identifiers, 'ipi')
  if (ipi) {
    lines.push(`IPI: ${formatIPIDisplay(ipi)}`)
  }

  const isni = getIdentifierValue(identifiers, 'isni')
  if (isni) {
    lines.push(`ISNI: ${formatISNIDisplay(isni)}`)
  }

  return lines.length > 0 ? lines.join('\n') : undefined
}

/**
 * Builds an external_identifiers array for API submission.
 * Only includes identifiers that have non-empty values.
 * Returns `undefined` when both inputs are empty/undefined, to distinguish
 * 'no identifier fields were shown' from 'user cleared all identifiers'.
 *
 * @param ipi - IPI value (can be formatted or raw), undefined if field not shown
 * @param isni - ISNI value (can be formatted or raw), undefined if field not shown
 * @returns Array of external identifiers, or undefined if no fields were provided
 */
export function buildExternalIdentifiers(
  ipi: string | undefined,
  isni: string | undefined,
): ExternalIdentifier[] | undefined {
  // If both inputs are undefined, no identifier fields were shown
  if (ipi === undefined && isni === undefined) {
    return undefined
  }

  const identifiers: ExternalIdentifier[] = []

  const parsedIpi = ipi ? parseIPIInput(ipi) : ''
  if (parsedIpi) {
    identifiers.push({
      identifier_type: 'ipi',
      identifier_value: parsedIpi,
    })
  }

  const parsedIsni = isni ? parseISNIInput(isni) : ''
  if (parsedIsni) {
    identifiers.push({
      identifier_type: 'isni',
      identifier_value: parsedIsni,
    })
  }

  return identifiers
}

export function areExternalIdentifiersEqual(
  a: ExternalIdentifier[] | undefined,
  b: ExternalIdentifier[] | undefined,
): boolean {
  const normalizedA = a?.length ? a : []
  const normalizedB = b?.length ? b : []

  if (normalizedA.length !== normalizedB.length) {
    return false
  }

  if (normalizedA.length === 0) {
    return true
  }

  const sortedA = [...normalizedA].sort((x, y) => {
    const xFull = x.identifier_type + x.identifier_value
    const yFull = y.identifier_type + y.identifier_value
    return xFull.localeCompare(yFull)
  })
  const sortedB = [...normalizedB].sort((x, y) => {
    const xFull = x.identifier_type + x.identifier_value
    const yFull = y.identifier_type + y.identifier_value
    return xFull.localeCompare(yFull)
  })

  return sortedA.every(
    (item, index) =>
      item.identifier_type === sortedB[index].identifier_type &&
      item.identifier_value === sortedB[index].identifier_value,
  )
}

export function validateIsrc(isrc: string): {
  valid: boolean
  reason?: string
} {
  if (isrc === '') {
    return { valid: true }
  }

  if (isrc.length !== 12) {
    return {
      valid: false,
      reason: 'Invalid length. ISRC should be 12 characters in length.',
    }
  }

  const isrcRegex = /[a-z]{2}\w{3}\d{7}/i

  if (!isrcRegex.test(isrc)) {
    return {
      valid: false,
      reason:
        'ISRC does not match expected format. ISRC should begin with a prefix code (2 letters followed by 3 alphanumeric characters), a year of reference (2 digits), and a designation code (5 digits).',
    }
  }

  return { valid: true }
}
